/**
 * Transfer packing workspace — touch + scanner + numpad from pre-pack.
 * Kiosk layout: fits 1080p (ASUS VT229H) without page scroll.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ClipboardList, Loader2 } from "lucide-react";

import { HubBreadcrumbTitleRow } from "@/components/HubPageTitle";
import { useNavigationTrail, usePageBreadcrumb } from "@/lib/navigationTrailContext";

import { apiFetch, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { InboundZplPrinterHeaderButton } from "@/pages/shipping/InboundZplPrinterHeaderButton";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { useAuth } from "@/components/AuthProvider";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import type { MrpSkuHoverHintFields } from "@/lib/mrp";
import { TransferLocationRoute } from "@/pages/manufacturing/transfersWorkbenchUi";
import { prepackTransferLocationProps } from "./prepackTransferLocations";
import { cn } from "@/lib/utils";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { isLaborOwnerDeparting } from "@/lib/labor/laborOwnerDeparture";
import { formatLaborLocationLabel } from "@/lib/labor/laborPageKey";
import { flashAndBumpToOverview } from "@/lib/labor/laborTimeoutHandlers";
import { useFloorPageScanCapture } from "@/lib/floorPageScanCapture";
import type { InventoryBox } from "@/lib/inventoryBoxApi";
import { useLaborSession } from "@/lib/labor/useLaborSession";
import { KittingInventoryDiscrepancyButton } from "@/pages/manufacturing/KittingInventoryDiscrepancyButton";
import { KittingInventoryBoxPickModal } from "@/pages/manufacturing/KittingInventoryBoxPickModal";
import { kittingMaterialScanTarget } from "@/pages/manufacturing/kittingScanModel";
import { transferFloorRequestToast } from "@/pages/manufacturing/transferFloorRequestToast";
import { WarehouseRecountModal } from "@/pages/warehouse/WarehouseRecountModal";

import { QUEUE_KEY } from "./PrePackQueuePage";
import { PrePackCartonOperatorStack } from "./PrePackCartonOperatorStack";
import { PREPACK_LABOR_FLASH_STORAGE_KEY } from "./prepackLaborFlash";
import { transferPackLaborPageKeyForOrder } from "./prepackTransferLaborPage";
import {
  addPrePackTransferAllocation,
  deletePrePackTransferAllocation,
  fetchPrePackTransfer,
  preparePrePackTransferInventoryContainer,
  fetchPrePackActiveTransferOperators,
  formatShipDate,
  prePackActiveTransferOperatorsQueryKey,
  printPrePackTransferCartonLabel,
  sealPrePackTransferBox,
  submitPrePackTransferShipLessRequest,
  unsealPrePackTransferBox,
  updatePrePackTransferAllocation,
  type TransferPackLine,
  type TransferPackWorkspace,
} from "./prepackApi";
import {
  applyTransferPackAllocationAdded,
  applyTransferPackAllocationRemoved,
  applyTransferPackAllocationUpdated,
  applyTransferPackPendingRequestCreated,
  applyTransferPackPendingRequestsCreated,
  applyTransferPackCartonLabelPrinted,
  applyTransferPackBoxSealed,
  applyTransferPackBoxUnsealed,
} from "./prepackTransferCache";
import { usePrepackZplPrinter } from "./prepackZplPrinter";
import {
  kioskMainPaneOrderClass,
  kioskNumpadPaneOrderClass,
  kioskNumpadSplitGridClass,
  PREPACK_TRANSFER_KIOSK_HEIGHT_CLASS,
  readStoredTransferPackNumpadSide,
  TransferPackCartonsPanel,
  TransferPackInputMode,
  TransferPackLineTile,
  TransferPackNumpad,
  TransferPackNumpadSideToggle,
  PREPACK_TRANSFER_NUMPAD_SIDE_STORAGE_KEY,
  TransferPackQuantityReadout,
  TransferPackShipLessPanel,
  transferShipLessEligibleLines,
  type TransferPackNumpadSide,
} from "./prepackTransferTouchUi";
import { applyTransferPackQuantityKey } from "./transferPackQuantityScan";

const transferQueryKey = (transferOrderId: number) =>
  ["prepack", "transfer", transferOrderId] as const;

const PACK_PAGE_HOVER_HINT =
  "Tap a line, scan its SKU, or scan an inventory container. Pack all puts that container into the open carton and keeps its id on the line, then the scanner is ready for the next container. Split still prints new labels for the pieces you pack. Enter a loose quantity on the keypad or a USB numpad, then press Enter to add to the lowest-number open carton. Tap the highlighted line again to leave it and scan the next container. The keypad Clear key only clears the quantity. "
  + "The first add opens carton 1 automatically. Seal when the carton is complete, print the label, then continue. "
  + "When the last carton is sealed the transfer becomes ready to ship, but you stay on this page so you can print labels — leave with the breadcrumb when finished. "
  + "Shipping administrators can unseal an earlier carton while a later one is open; new scans still land in the lowest open carton. "
  + "Use View contents on sealed cartons to inspect packed lines. Left is how many units still need to go into cartons.";

function focusScanInput(input: HTMLInputElement | null): void {
  if (input == null) return;
  requestAnimationFrame(() => {
    input.focus({ preventScroll: true });
  });
}

export function PrePackTransferPage(): JSX.Element {
  const params = useParams<{ transferOrderId: string }>();
  const transferOrderId = Number.parseInt(params.transferOrderId ?? "", 10);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const scanInputRef = useRef<HTMLInputElement>(null);
  const submitQuantityLockRef = useRef(false);
  const selectedInventoryBoxHexesRef = useRef<string[]>([]);
  const quantityScanBufferRef = useRef("");

  const [inputMode, setInputMode] = useState<TransferPackInputMode>("scan");
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [quantityDraft, setQuantityDraft] = useState("");
  const [scannedInventoryBox, setScannedInventoryBox] = useState<InventoryBox | null>(null);
  const [recountOpen, setRecountOpen] = useState(false);
  const [editingAllocationId, setEditingAllocationId] = useState<string | null>(null);
  const [numpadSide, setNumpadSide] = useState<TransferPackNumpadSide>(readStoredTransferPackNumpadSide);
  const [printingBoxNumber, setPrintingBoxNumber] = useState<number | null>(null);
  const [unsealPendingBoxId, setUnsealPendingBoxId] = useState<number | null>(null);

  const isShippingAdmin = useHubModuleAdmin("shipping");
  const { session } = useAuth();
  const viewerUserId = session?.user?.id ?? null;
  const viewerKnown = viewerUserId !== null && viewerUserId !== "";
  const {
    station,
    setStation,
    selectedPrinter: selectedZplPrinter,
    unavailableMessage, documents,
  } = usePrepackZplPrinter();

  const bumpTransferPackLaborToQueueWithFlash = useCallback(
    (message: string) => {
      flashAndBumpToOverview({
        navigate,
        to: "/shipping/pre-pack",
        storageKey: PREPACK_LABOR_FLASH_STORAGE_KEY,
        message,
        replace: true,
      });
    },
    [navigate],
  );

  const laborPage = useMemo(
    () =>
      Number.isFinite(transferOrderId) && transferOrderId > 0
        ? transferPackLaborPageKeyForOrder(transferOrderId)
        : "",
    [transferOrderId],
  );
  const labor = useLaborSession({
    page: laborPage,
    domain: "shipping",
    moduleGate: "shipping",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled("prepack"),
    workLabel: "Transfer Pack",
    autoStart: laborPage !== "",
    requirePersistedClockIn: true,
    onIdleTimeout: () =>
      bumpTransferPackLaborToQueueWithFlash(
        "Clocked out due to inactivity on this transfer. Open it again from the queue when you return.",
      ),
    onPersistedSessionLost: () =>
      bumpTransferPackLaborToQueueWithFlash(
        "Your hub labor session for this transfer ended (timeout, error, or the session was closed on the server). Use the Pre-Pack queue breadcrumb to return, then open the transfer again to clock back in.",
      ),
    onSessionEndedRemotely: () =>
      bumpTransferPackLaborToQueueWithFlash(
        "Clocked out from another tab or the session was closed on the server. Open the transfer again from the queue when you want to continue.",
      ),
    onStopped: () =>
      bumpTransferPackLaborToQueueWithFlash(
        "Clocked out. Open the transfer again from the queue when you want to continue.",
      ),
  });
  const laborGateReady = laborPage !== "" && labor.gateReady;

  const laborGateConflictBumpRef = useRef(false);
  useEffect(() => {
    if (!Number.isFinite(transferOrderId) || transferOrderId <= 0) return;
    if (labor.loading) return;
    if (isLaborOwnerDeparting()) return;
    if (labor.activeOnOtherPage) {
      if (!laborGateConflictBumpRef.current) {
        laborGateConflictBumpRef.current = true;
        bumpTransferPackLaborToQueueWithFlash(
          `Finish clocking out of ${formatLaborLocationLabel(labor.activeOnOtherPage)} before opening this transfer. Return to the queue, stop that session, then try again.`,
        );
      }
      return;
    }
    laborGateConflictBumpRef.current = false;
  }, [transferOrderId, labor.loading, labor.activeOnOtherPage, bumpTransferPackLaborToQueueWithFlash]);

  const laborGateFailureBumpRef = useRef(false);
  useEffect(() => {
    if (labor.gateReady) {
      laborGateFailureBumpRef.current = false;
    }
  }, [labor.gateReady]);

  useEffect(() => {
    if (!Number.isFinite(transferOrderId) || transferOrderId <= 0) return;
    if (labor.loading || labor.gateReady) return;
    if (labor.activeOnOtherPage) return;
    if (!labor.banner?.message) return;
    if (laborGateFailureBumpRef.current) return;
    laborGateFailureBumpRef.current = true;
    bumpTransferPackLaborToQueueWithFlash(labor.banner.message);
  }, [
    transferOrderId,
    labor.loading,
    labor.gateReady,
    labor.activeOnOtherPage,
    labor.banner?.message,
    bumpTransferPackLaborToQueueWithFlash,
  ]);

  const touchLaborActivity = useCallback((): void => {
    labor.touchActivity();
  }, [labor]);

  const transferQuery = useQuery({
    queryKey: transferQueryKey(transferOrderId),
    queryFn: () => fetchPrePackTransfer(transferOrderId),
    enabled: Number.isFinite(transferOrderId) && transferOrderId > 0 && laborGateReady,
    staleTime: 5_000,
  });

  const transferOperatorsQuery = useQuery({
    queryKey: prePackActiveTransferOperatorsQueryKey,
    queryFn: fetchPrePackActiveTransferOperators,
    enabled: Number.isFinite(transferOrderId) && transferOrderId > 0 && laborGateReady,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });

  const transferOperators = useMemo(
    () =>
      (transferOperatorsQuery.data ?? []).filter((operator) => operator.transferOrderId === transferOrderId),
    [transferOperatorsQuery.data, transferOrderId],
  );

  const showOtherOperatorsOnTransfer =
    transferOperators.length > 0 &&
    (!viewerKnown || transferOperators.some((operator) => operator.userId !== viewerUserId));

  const transfer = transferQuery.data;
  const breadcrumbLabel = useMemo(() => {
    if (transfer != null) {
      return transfer.transfer_number ?? `Transfer #${String(transfer.id)}`;
    }
    if (Number.isFinite(transferOrderId) && transferOrderId > 0) {
      return `Transfer #${String(transferOrderId)}`;
    }
    return "Transfer pack";
  }, [transfer, transferOrderId]);
  usePageBreadcrumb(breadcrumbLabel);
  const { trail } = useNavigationTrail();
  const openBox = transfer?.open_box ?? null;

  const lineSkus = useMemo(() => {
    if (transfer == null) return [];
    return [...new Set(
      transfer.lines
        .map((line) => line.sku_id?.trim() ?? "")
        .filter((sku) => sku !== ""),
    )].sort((left, right) => left.localeCompare(right));
  }, [transfer]);

  const skuHintsQuery = useQuery<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }, ApiError>({
    queryKey: ["prepack", "transfer-sku-hints", transferOrderId, lineSkus.join(",")],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: lineSkus.join(",") },
      }),
    enabled: lineSkus.length > 0,
    staleTime: 60_000,
  });

  const skuHints = skuHintsQuery.data?.sku_hover_hints ?? {};

  const linesBySku = useMemo(() => {
    const map = new Map<string, TransferPackLine>();
    for (const line of transfer?.lines ?? []) {
      const sku = line.sku_id?.trim();
      if (sku != null && sku !== "") map.set(sku, line);
    }
    return map;
  }, [transfer?.lines]);

  const selectedLine = selectedSku != null ? linesBySku.get(selectedSku) ?? null : null;
  const editingAllocation = useMemo(() => {
    if (editingAllocationId == null) return null;
    for (const box of transfer?.boxes ?? []) {
      if (box.sealed) continue;
      const allocation = box.allocations.find((row) => String(row.id) === String(editingAllocationId));
      if (allocation != null) return allocation;
    }
    return null;
  }, [editingAllocationId, transfer?.boxes]);

  const patchTransferCache = useCallback((
    updater: (current: TransferPackWorkspace) => TransferPackWorkspace | null,
  ) => {
    queryClient.setQueryData<TransferPackWorkspace | undefined>(
      transferQueryKey(transferOrderId),
      (current) => {
      if (current == null) return current;
      return updater(current) ?? current;
    });
  }, [queryClient, transferOrderId]);

  const returnToScanMode = useCallback(() => {
    selectedInventoryBoxHexesRef.current = [];
    quantityScanBufferRef.current = "";
    setInputMode("scan");
    setQuantityDraft("");
    setSelectedSku(null);
    setEditingAllocationId(null);
    focusScanInput(scanInputRef.current);
  }, []);

  const selectSku = useCallback((sku: string, preserveInventoryBoxes = false) => {
    if (!preserveInventoryBoxes) selectedInventoryBoxHexesRef.current = [];
    touchLaborActivity();
    const normalized = sku.trim();
    if (normalized === "") return;
    const line = linesBySku.get(normalized);
    if (line == null) {
      showToast({ tone: "error", message: `SKU ${normalized} is not on this transfer.` });
      return;
    }
    setEditingAllocationId(null);
    setSelectedSku(normalized);
    quantityScanBufferRef.current = "";
    setQuantityDraft("");
    setInputMode("quantity");
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [linesBySku, showToast, touchLaborActivity]);

  const handleScan = useCallback((scanned: string) => {
    const material = kittingMaterialScanTarget(scanned);
    if (material == null) {
      showToast({ tone: "error", message: "Could not read that scan as a SKU or inventory container." });
      focusScanInput(scanInputRef.current);
      return;
    }
    if (material.kind === "sku") {
      selectSku(material.sku);
      return;
    }
    void preparePrePackTransferInventoryContainer(transferOrderId, material.hex)
      .then((prepared) => {
        const box = prepared.box;
        if (!linesBySku.has(box.sku.trim())) {
          throw new Error(`SKU ${box.sku} is not on this transfer.`);
        }
        setScannedInventoryBox(box);
      })
      .catch((error) => {
        showToast({
          tone: "error",
          message: error instanceof ApiError ? error.message : String(error),
        });
        focusScanInput(scanInputRef.current);
      });
  }, [linesBySku, selectSku, showToast, transferOrderId]);

  useFloorPageScanCapture({
    enabled: scannedInventoryBox == null && !recountOpen,
    captureUnfocusedHid: inputMode === "scan",
    autoFocus: inputMode === "scan",
    inputRef: scanInputRef,
    onScan: handleScan,
  });

  const startEditingAllocation = useCallback((allocation: {
    id: string;
    sku_id: string | null;
    quantity: number;
  }) => {
    selectedInventoryBoxHexesRef.current = [];
    touchLaborActivity();
    const normalized = allocation.sku_id?.trim() ?? "";
    if (normalized === "") return;
    const line = linesBySku.get(normalized);
    if (line == null) {
      showToast({ tone: "error", message: `SKU ${normalized} is not on this transfer.` });
      return;
    }
    setEditingAllocationId(allocation.id);
    setSelectedSku(normalized);
    setQuantityDraft(String(allocation.quantity));
    setInputMode("quantity");
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [linesBySku, showToast, touchLaborActivity]);

  const addMutation = useMutation({
    mutationFn: (body: { sku_id: string; quantity: number; inventory_box_hexes?: string[] }) =>
      addPrePackTransferAllocation(transferOrderId, body),
    onSuccess: (response, variables) => {
      selectedInventoryBoxHexesRef.current = [];
      patchTransferCache((current) => {
        let next = applyTransferPackAllocationAdded(
          current,
          response.allocation,
          response.box,
          variables.quantity,
        );
        if (response.packing_quantity_request != null) {
          next = applyTransferPackPendingRequestCreated(next, response.allocation.transfer_order_item_id);
        }
        return next;
      });
      const packed = response.packed_inventory_containers ?? [];
      const prefix = packed.length === 0
        ? "Added to carton."
        : packed.length === 1
          ? `Packed container ${packed[0]?.short_code ?? ""} into carton #${String(response.box.box_number)}.`
          : `Packed containers ${packed.map((row) => row.short_code).join(", ")} into carton #${String(response.box.box_number)}.`;
      showToast({
        tone: "success",
        message: transferFloorRequestToast(prefix, response),
      });
      if ((variables.inventory_box_hexes?.length ?? 0) > 0) {
        returnToScanMode();
        return;
      }
      setQuantityDraft("");
      setEditingAllocationId(null);
      setInputMode("quantity");
    },
    onError: (error: ApiError) => {
      selectedInventoryBoxHexesRef.current = [];
      showToast({ tone: "error", message: error.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (body: { allocationId: string; quantity: number }) =>
      updatePrePackTransferAllocation(transferOrderId, body.allocationId, { quantity: body.quantity }),
    onSuccess: (response) => {
      patchTransferCache((current) => {
        let next = applyTransferPackAllocationUpdated(
          current,
          response.allocation.id,
          response.allocation.quantity,
        );
        if (next != null && response.packing_quantity_request != null) {
          next = applyTransferPackPendingRequestCreated(next, response.allocation.transfer_order_item_id);
        }
        return next;
      });
      setQuantityDraft("");
      setEditingAllocationId(null);
      showToast({
        tone: "success",
        message: transferFloorRequestToast("Updated carton quantity.", response),
      });
      setInputMode("quantity");
    },
    onError: (error: ApiError) => showToast({ tone: "error", message: error.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: (allocationId: string) => deletePrePackTransferAllocation(transferOrderId, allocationId),
    onSuccess: (response, allocationId) => {
      patchTransferCache((current) => applyTransferPackAllocationRemoved(current, allocationId));
      setEditingAllocationId((current) => {
        if (current != null && String(current) === String(allocationId)) {
          setSelectedSku(null);
          setQuantityDraft("");
          setInputMode("scan");
          focusScanInput(scanInputRef.current);
          return null;
        }
        return current;
      });
      const released = response.released_inventory_containers ?? [];
      const releasedLabel = released.map((container) => container.short_code).join(", ");
      showToast({
        tone: "success",
        message: releasedLabel === ""
          ? "Removed from carton."
          : `Removed from carton. ${releasedLabel} can be scanned again.`,
      });
    },
    onError: (error: ApiError) => showToast({ tone: "error", message: error.message }),
  });

  const quantityMutationPending =
    addMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const packScannedContainers = useCallback((
    sku: string,
    quantity: number,
    inventoryBoxHexes: string[],
  ) => {
    setScannedInventoryBox(null);
    touchLaborActivity();
    const normalized = sku.trim();
    if (!linesBySku.has(normalized)) {
      showToast({ tone: "error", message: `SKU ${normalized} is not on this transfer.` });
      focusScanInput(scanInputRef.current);
      return;
    }
    if (!Number.isInteger(quantity) || quantity < 1 || inventoryBoxHexes.length === 0) {
      showToast({ tone: "error", message: "That container has nothing to pack." });
      focusScanInput(scanInputRef.current);
      return;
    }
    if (submitQuantityLockRef.current || quantityMutationPending) return;
    submitQuantityLockRef.current = true;
    addMutation.mutate(
      {
        sku_id: normalized,
        quantity,
        inventory_box_hexes: inventoryBoxHexes,
      },
      { onSettled: () => { submitQuantityLockRef.current = false; } },
    );
  }, [addMutation, linesBySku, quantityMutationPending, showToast, touchLaborActivity]);

  const sealMutation = useMutation({
    mutationFn: (boxId: number) => sealPrePackTransferBox(transferOrderId, boxId),
    onSuccess: (response) => {
      patchTransferCache((current) => {
        const sealed = applyTransferPackBoxSealed(current, response.box.id);
        if (sealed == null) return current;
        if (!response.auto_advanced) return sealed;
        // Stay on this page so the operator can print carton labels (and any other
        // remaining steps) before leaving manually via the breadcrumb.
        return { ...sealed, status: "ready_to_ship" };
      });
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
      if (response.auto_advanced) {
        showToast({
          tone: "success",
          message: `Carton #${String(response.box.box_number)} sealed. Transfer marked ready to ship — print labels if needed, then leave when finished.`,
        });
      } else {
        showToast({ tone: "success", message: `Carton #${String(response.box.box_number)} sealed.` });
      }
      returnToScanMode();
    },
    onError: (error: ApiError) => showToast({ tone: "error", message: error.message }),
  });

  const printLabelMutation = useMutation({
    mutationFn: (body: { boxNumber: number; boxId: number; printer: string }) =>
      printPrePackTransferCartonLabel(transferOrderId, body.boxNumber, body.printer),
    onMutate: ({ boxNumber }) => {
      setPrintingBoxNumber(boxNumber);
    },
    onSuccess: (data, { boxNumber, boxId }) => {
      patchTransferCache((current) => applyTransferPackCartonLabelPrinted(current, boxId) ?? current);
      const labelCount = data.labelCount ?? 1;
      showToast({
        tone: "success",
        message:
          labelCount > 1
            ? `Carton #${String(boxNumber)}: ${String(labelCount)} labels sent to printer.`
            : `Carton #${String(boxNumber)} label sent to printer.`,
      });
    },
    onError: (error: ApiError) => showToast({ tone: "error", message: error.message }),
    onSettled: () => setPrintingBoxNumber(null),
  });

  const unsealMutation = useMutation({
    mutationFn: (boxId: number) => unsealPrePackTransferBox(transferOrderId, boxId),
    onMutate: (boxId) => {
      setUnsealPendingBoxId(boxId);
    },
    onSuccess: (response) => {
      patchTransferCache((current) =>
        applyTransferPackBoxUnsealed(current, response.box.id, response.status_reverted) ?? current,
      );
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
      showToast({
        tone: "success",
        message: response.status_reverted
          ? `Carton #${String(response.box.box_number)} unsealed. Transfer returned to packing.`
          : `Carton #${String(response.box.box_number)} unsealed.`,
      });
      returnToScanMode();
    },
    onError: (error: ApiError) => showToast({ tone: "error", message: error.message }),
    onSettled: () => setUnsealPendingBoxId(null),
  });

  const shipLessMutation = useMutation({
    mutationFn: async (lines: readonly TransferPackLine[]) => {
      for (const line of lines) {
        await submitPrePackTransferShipLessRequest(transferOrderId, {
          transfer_order_item_id: line.id,
        });
      }
      return lines;
    },
    onSuccess: (lines) => {
      patchTransferCache((current) => applyTransferPackPendingRequestsCreated(
        current,
        lines.map((line) => line.id),
      ));
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
      const message = lines.length === 1
        ? `Pack quantity request submitted for ${lines[0]?.sku_id ?? "line"}. Waiting for approval.`
        : `Pack quantity requests submitted for ${String(lines.length)} lines. Waiting for approval.`;
      showToast({ tone: "success", message });
      try {
        sessionStorage.setItem(PREPACK_LABOR_FLASH_STORAGE_KEY, message);
      } catch {
        /* private mode or storage quota */
      }
      navigate("/shipping/pre-pack", { replace: true });
    },
    onError: (error: ApiError) => showToast({ tone: "error", message: error.message }),
  });

  const handlePrintLabel = useCallback(
    (boxNumber: number) => {
      const box = transfer?.boxes.find((row) => row.box_number === boxNumber);
      if (box == null) {
        showToast({ tone: "error", message: "Carton not found." });
        return;
      }
      printLabelMutation.mutate({
        boxNumber,
        boxId: box.id,
        printer: selectedZplPrinter,
      });
    },
    [printLabelMutation, selectedZplPrinter, showToast, transfer?.boxes],
  );

  const submitQuantity = useCallback(() => {
    touchLaborActivity();
    if (submitQuantityLockRef.current || quantityMutationPending) return;
    if (selectedSku == null || selectedLine == null) {
      showToast({ tone: "error", message: "Select a transfer line first." });
      return;
    }
    const qty = Number.parseInt(quantityDraft, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      showToast({ tone: "error", message: "Enter a quantity greater than zero." });
      return;
    }
    submitQuantityLockRef.current = true;
    if (editingAllocationId != null) {
      updateMutation.mutate(
        { allocationId: editingAllocationId, quantity: qty },
        { onSettled: () => { submitQuantityLockRef.current = false; } },
      );
      return;
    }
    addMutation.mutate(
      {
        sku_id: selectedSku,
        quantity: qty,
        inventory_box_hexes: selectedInventoryBoxHexesRef.current,
      },
      { onSettled: () => { submitQuantityLockRef.current = false; } },
    );
  }, [
    addMutation,
    editingAllocation,
    editingAllocationId,
    quantityDraft,
    quantityMutationPending,
    selectedLine,
    selectedSku,
    showToast,
    transfer,
    updateMutation,
    touchLaborActivity,
  ]);

  const appendDigit = useCallback((digit: string) => {
    touchLaborActivity();
    setQuantityDraft((current) => {
      if (current === "0") return digit;
      if (current.length >= 6) return current;
      return `${current}${digit}`;
    });
  }, [touchLaborActivity]);

  const clearQuantity = useCallback(() => {
    touchLaborActivity();
    quantityScanBufferRef.current = "";
    setQuantityDraft("");
  }, [touchLaborActivity]);

  const backspaceQuantity = useCallback(() => {
    touchLaborActivity();
    const buffer = quantityScanBufferRef.current;
    quantityScanBufferRef.current = /^\d+$/.test(buffer) ? buffer.slice(0, -1) : "";
    setQuantityDraft((current) => current.slice(0, -1));
  }, [touchLaborActivity]);

  useEffect(() => {
    if (inputMode !== "scan") return;
    focusScanInput(scanInputRef.current);
  }, [inputMode, transfer?.id]);

  useEffect(() => {
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(PREPACK_TRANSFER_NUMPAD_SIDE_STORAGE_KEY, numpadSide);
    } catch {
      /* ignore */
    }
  }, [numpadSide]);

  useEffect(() => {
    if (inputMode !== "quantity" || selectedSku == null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const inPackNumpad = target.closest("[data-transfer-pack-numpad]") != null;
      const blocksPackKeys =
        target !== scanInputRef.current
        && (
          target.closest("input, textarea, select") != null
          || (target.closest("button, a") != null && !inPackNumpad)
        );

      if (blocksPackKeys) return;

      const effect = applyTransferPackQuantityKey(quantityScanBufferRef.current, event.key);
      if (effect.type === "ignore") return;

      event.preventDefault();
      if (effect.type === "scan") {
        quantityScanBufferRef.current = "";
        handleScan(effect.scanned);
        return;
      }
      if (effect.type === "submit-quantity") {
        quantityScanBufferRef.current = "";
        if (!quantityMutationPending && !submitQuantityLockRef.current) {
          submitQuantity();
        }
        return;
      }
      quantityScanBufferRef.current = effect.buffer;
      if (effect.type === "quantity-digit") {
        if (effect.rewindDigits > 0) {
          setQuantityDraft((current) => current.slice(0, Math.max(0, current.length - effect.rewindDigits)));
        }
        appendDigit(effect.digit);
        return;
      }
      if (effect.type === "quantity-backspace") {
        setQuantityDraft((current) => current.slice(0, -1));
        return;
      }
      if (effect.rewindDigits > 0) {
        setQuantityDraft((current) => current.slice(0, Math.max(0, current.length - effect.rewindDigits)));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [appendDigit, handleScan, inputMode, quantityMutationPending, selectedSku, submitQuantity]);

  if (!Number.isFinite(transferOrderId) || transferOrderId <= 0) {
    return <p className="py-8 text-center text-sm text-rose-700">Invalid transfer order id.</p>;
  }

  if (!laborGateReady && laborPage !== "") {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 text-slate-600">
        <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
        <p className="text-sm">Clocking in…</p>
      </div>
    );
  }

  if (transferQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading transfer…
      </div>
    );
  }

  if (transferQuery.isError || transfer == null) {
    return (
      <p className="py-8 text-center text-sm text-rose-700">
        {transferQuery.error instanceof ApiError
          ? transferQuery.error.message
          : "Transfer not found or not packable from pre-pack."}
      </p>
    );
  }

  const title = transfer.transfer_number ?? `Transfer #${String(transfer.id)}`;
  const sealedBoxCount = transfer.boxes.filter((box) => box.sealed).length;
  const canPack =
    transfer != null
    && transfer.status === "packing"
    && !transfer.is_fully_packed;
  const shipLessLines = transferShipLessEligibleLines(transfer);
  const numpadOnRight = numpadSide === "right";

  return (
    <div
      className={cn(
        "-my-6 flex min-h-0 flex-col overflow-hidden",
        PREPACK_TRANSFER_KIOSK_HEIGHT_CLASS,
      )}
    >
      <input
        ref={scanInputRef}
        type="text"
        autoComplete="off"
        aria-label="Scan SKU or inventory container"
        className="sr-only"
        data-sgs-scan-target=""
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          if (inputMode === "quantity") return;
          event.preventDefault();
          const scanned = event.currentTarget.value.trim();
          event.currentTarget.value = "";
          if (scanned === "") return;
          handleScan(scanned);
        }}
      />

      <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-slate-50 px-1 py-2">
        <div className="min-w-0 flex-1">
          {trail.length > 0 ? (
            <HubBreadcrumbTitleRow
              className="min-w-0"
              title={
                <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="truncate text-lg font-semibold text-slate-900">{title}</span>
                  {sealedBoxCount > 0 ? <Badge tone="neutral">{sealedBoxCount} sealed</Badge> : null}
                  {transfer.pending_pack_qty_request_count > 0 ? (
                    <Badge tone="warning">Approval pending</Badge>
                  ) : null}
                </span>
              }
              helpGlyph={<HoverHintWithGlyph hoverHint={PACK_PAGE_HOVER_HINT} />}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <h1 className="truncate text-lg font-semibold text-slate-900">{title}</h1>
              <HoverHintWithGlyph hoverHint={PACK_PAGE_HOVER_HINT} />
              {sealedBoxCount > 0 ? <Badge tone="neutral">{sealedBoxCount} sealed</Badge> : null}
              {transfer.pending_pack_qty_request_count > 0 ? (
                <Badge tone="warning">Approval pending</Badge>
              ) : null}
            </div>
          )}
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0 text-xs text-slate-600">
            <TransferLocationRoute
              originLocation={prepackTransferLocationProps(
                transfer.origin_location_id,
                transfer.origin_location_abbreviation,
                transfer.origin_location_mrp_site_code,
              )}
              originLocationId={transfer.origin_location_id}
              destinationLocation={prepackTransferLocationProps(
                transfer.destination_location_id,
                transfer.destination_location_abbreviation,
                transfer.destination_location_mrp_site_code,
              )}
              destinationLocationId={transfer.destination_location_id}
            />
            {transfer.ship_by_date != null ? (
              <span>Ship by {formatShipDate(transfer.ship_by_date)}</span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-end gap-2">
          {showOtherOperatorsOnTransfer ? (
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                {viewerKnown ? "Other operators on this transfer" : "Operators on this transfer"}
              </span>
              <PrePackCartonOperatorStack
                operators={transferOperators}
                viewerUserId={viewerUserId}
                omitViewer={viewerKnown}
              />
            </div>
          ) : null}
          <InboundZplPrinterHeaderButton
            station={station}
            onStationChange={setStation}
            unavailableMessage={unavailableMessage}
            className="h-12"
          
      documents={documents}
    />
          <Button
            type="button"
            variant="secondary"
            className="h-12"
            onClick={() => setRecountOpen(true)}
          >
            <ClipboardList className="mr-1.5 h-4 w-4" aria-hidden />
            Recount / label
          </Button>
          <KittingInventoryDiscrepancyButton
            initialSku={selectedSku ?? ""}
            transferOrderId={transferOrderId}
            transferOrderItemId={selectedLine?.id ?? null}
            className="h-12"
          />
          <TransferPackNumpadSideToggle side={numpadSide} onSideChange={setNumpadSide} />
        </div>
      </header>

      <section
        aria-label="Transfer lines"
        className="shrink-0 border-b border-slate-200 bg-white px-1 py-1.5"
      >
        <div className="overflow-x-auto overflow-y-hidden pb-0.5">
          <div className="grid w-max auto-cols-[13.5rem] grid-flow-col gap-2">
            {transfer.lines.map((line) => {
              const sku = line.sku_id?.trim() ?? "";
              return (
                <TransferPackLineTile
                  key={line.id}
                  line={line}
                  hint={sku !== "" ? skuHints[sku] : undefined}
                  selected={selectedSku === sku}
                  disabled={!canPack || quantityMutationPending}
                  kiosk
                  onSelect={() => {
                    if (selectedSku === sku && (inputMode === "quantity" || editingAllocationId != null)) {
                      returnToScanMode();
                      return;
                    }
                    selectSku(sku);
                  }}
                />
              );
            })}
          </div>
        </div>
      </section>

      <div className={kioskNumpadSplitGridClass(numpadOnRight)}>
        <section
          aria-label="Cartons"
          className={cn(
            "flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white p-2",
            kioskMainPaneOrderClass(numpadOnRight),
          )}
        >
          <div className="flex shrink-0 flex-wrap items-end justify-between gap-x-2 gap-y-1 border-b border-slate-100 pb-1.5">
            <h2 className="shrink-0 text-sm font-semibold text-slate-900">Cartons</h2>
            <TransferPackShipLessPanel
              lines={shipLessLines}
              disabled={shipLessMutation.isPending || transfer.pending_pack_qty_request_count > 0}
              isSubmitting={shipLessMutation.isPending}
              onSubmitAll={async (lines) => {
                await shipLessMutation.mutateAsync(lines);
              }}
            />
          </div>

          <div className="h-0 min-h-0 flex-1 overflow-hidden">
            <TransferPackCartonsPanel
              boxes={transfer.boxes}
              canPack={canPack}
              openBox={openBox}
              skuHints={skuHints}
              editingAllocationId={editingAllocationId}
              quantityMutationPending={quantityMutationPending}
              sealPending={sealMutation.isPending}
              printingBoxNumber={printingBoxNumber}
              unsealPendingBoxId={unsealPendingBoxId}
              showUnseal={isShippingAdmin}
              onSeal={(boxId) => sealMutation.mutate(boxId)}
              onPrintLabel={handlePrintLabel}
              onUnseal={(boxId) => unsealMutation.mutate(boxId)}
              onEditAllocation={startEditingAllocation}
              onDeleteAllocation={(allocationId) => deleteMutation.mutate(allocationId)}
              onLaborActivity={touchLaborActivity}
            />
          </div>
        </section>

        <section
          aria-label="Pack quantity"
          className={cn(
            "flex min-h-0 flex-col gap-2 overflow-hidden rounded-lg border border-slate-200 bg-white p-2",
            kioskNumpadPaneOrderClass(numpadOnRight),
          )}
        >
          {!canPack ? (
            <p className="text-sm text-slate-600">
              {transfer.pending_pack_qty_request_count > 0
                ? "Resolve pending pack quantity approvals before this transfer can ship."
                : shipLessLines.length > 0
                  ? "Use the ship-less requests above, or unseal a carton to keep packing."
                : transfer.is_fully_packed
                  ? "All lines are allocated across sealed cartons. Print carton labels if needed, then use the breadcrumb to leave."
                  : "Packing is blocked for this transfer."}
            </p>
          ) : (
            <>
              <TransferPackQuantityReadout
                kiosk
                selectedSku={selectedSku}
                quantityDraft={quantityDraft}
                editingCartonLine={editingAllocationId != null}
              />
              <div className="min-h-0 flex-1">
                <TransferPackNumpad
                  fillHeight
                  fillHeightFrom="sm"
                  disabled={selectedSku == null || quantityMutationPending}
                  onDigit={(digit) => {
                    quantityScanBufferRef.current = "";
                    appendDigit(digit);
                  }}
                  onClear={clearQuantity}
                  onBackspace={backspaceQuantity}
                  onEnter={submitQuantity}
                />
              </div>
            </>
          )}
        </section>
      </div>
      <KittingInventoryBoxPickModal
        box={scannedInventoryBox}
        printer={selectedZplPrinter}
        station={station}
        documents={documents}
        onStationChange={setStation}
        unavailableMessage={unavailableMessage}
        actionLabel="Pack"
        onCancel={() => {
          setScannedInventoryBox(null);
          focusScanInput(scanInputRef.current);
        }}
        onPickQuantity={packScannedContainers}
      />
      <WarehouseRecountModal
        open={recountOpen}
        initialSku={selectedSku}
        onClose={() => {
          setRecountOpen(false);
          focusScanInput(scanInputRef.current);
        }}
      />
    </div>
  );
}
